import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, useToast, errText } from '../ui';

const STEPS = [
  { n: 1, key: 'database', label: 'create_database' },
  { n: 2, key: 'seed', label: 'seed_data' },
  { n: 3, key: 'configure', label: 'register_tenant' },
  { n: 4, key: 'admin', label: 'admin_credentials' },
  { n: 5, key: 'org', label: 'organization_profile' },
];

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

function cleanDbPreview(clientName) {
  return `${(clientName || '').toLowerCase().replace(/[^a-z0-9]/g, '')}_school`;
}

export default function TenantOnboardWizard() {
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({}); // { 1: true, 2: true, ... }

  const [clientName, setClientName] = useState('');
  const [tenantDbName, setTenantDbName] = useState('');
  const [seedStatus, setSeedStatus] = useState(null);

  const [configureForm, setConfigureForm] = useState({
    clientCode: '', clientName: '', dbServer: '', emailId: '', mobileNo: '',
    address: '', websiteLink: '', expiryDate: '', logoPath: '', noOfUsers: '',
    fineYear: '2026-2027', isPending: false,
  });

  const [adminForm, setAdminForm] = useState({
    newUsername: '', password: '', userType: 'Admin', expiryDate: '',
  });
  const [pwError, setPwError] = useState('');

  const [orgForm, setOrgForm] = useState({
    organisationName: '', email: '', mobileNo: '', landlineNo: '', address: '',
  });

  const markDone = (n) => setDone((d) => ({ ...d, [n]: true }));

  // ---- Step 1 ---------------------------------------------------------
  const runStep1 = async () => {
    setBusy(true);
    try {
      const res = await tenantApi.createDatabase(clientName);
      setTenantDbName(res.tenantDbName);
      setConfigureForm((f) => ({ ...f, clientName, tenantDbName: res.tenantDbName }));
      toast.success(res.msg || 'Database created.');
      markDone(1);
      setStep(2);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 2 ---------------------------------------------------------
  const runStep2 = async () => {
    setBusy(true);
    try {
      const res = await tenantApi.runSeed(tenantDbName);
      toast.success(res.msg || 'Seeded.');
      const status = await tenantApi.getSeedStatus(tenantDbName);
      setSeedStatus(status);
      markDone(2);
      setStep(3);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 3 ---------------------------------------------------------
  const runStep3 = async () => {
    setBusy(true);
    try {
      const payload = {
        ...configureForm,
        tenantDbName,
        noOfUsers: configureForm.noOfUsers ? Number(configureForm.noOfUsers) : undefined,
        expiryDate: configureForm.expiryDate || undefined,
        dbServer: configureForm.dbServer || undefined,
      };
      const res = await tenantApi.registerTenant(payload);
      toast.success(res.msg || 'Tenant registered.');
      markDone(3);
      setStep(4);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 4 ---------------------------------------------------------
  const runStep4 = async () => {
    if (!PASSWORD_RULE.test(adminForm.password)) {
      setPwError('Min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special character.');
      return;
    }
    setPwError('');
    setBusy(true);
    try {
      const res = await tenantApi.updateAdminUser({ ...adminForm, tenantDbName });
      toast.success(res.msg || 'Admin credentials set.');
      markDone(4);
      setStep(5);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 5 ---------------------------------------------------------
  const runStep5 = async () => {
    setBusy(true);
    try {
      const res = await tenantApi.setOrganization({ ...orgForm, tenantDbName });
      toast.success(res.msg || 'Organization profile saved.');
      markDone(5);
      toast.success(`Tenant "${configureForm.clientCode}" fully provisioned.`, '// PROVISIONING COMPLETE');
      navigate(`/admin/tenants/${configureForm.clientCode}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const canJump = (n) => n === 1 || done[n - 1] || n <= step;

  return (
    <>
      <h1 className="page-title">onboard_wizard</h1>
      <p className="page-sub">
        Runs Steps 1–5 sequentially. There is no atomic rollback across steps — a BLL-level{' '}
        <code>ProvisionTenant()</code> shortcut exists but isn&apos;t wired to an endpoint yet, so each
        step below is a separate, resumable call.
      </p>

      <div className="stepper">
        {STEPS.map((s) => (
          <button
            key={s.n}
            className={`stepper__item ${step === s.n ? 'active' : ''} ${done[s.n] ? 'done' : ''}`}
            onClick={() => canJump(s.n) && setStep(s.n)}
            style={{ cursor: canJump(s.n) ? 'pointer' : 'not-allowed', background: 'none' }}
          >
            <span className="n">{done[s.n] ? '✓' : s.n}</span>{s.label}
          </button>
        ))}
      </div>

      {step === 1 && (
        <Panel title="// STEP_1 — create_database">
          <div className="field">
            <label>clientName</label>
            <input
              className="term-input"
              placeholder="GreenwoodSchool"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
            <div className="field-hint">
              will resolve to: <b>{cleanDbPreview(clientName) || '—'}</b>
            </div>
          </div>
          <button className="term-btn term-btn--solid" disabled={busy || !clientName} onClick={runStep1}>
            {busy ? 'running...' : 'run_step_1 →'}
          </button>
        </Panel>
      )}

      {step === 2 && (
        <Panel title="// STEP_2 — seed_data">
          <p style={{ fontSize: 12.5, color: 'var(--text-dim)', marginBottom: 14 }}>
            target database: <b style={{ color: 'var(--green)' }}>{tenantDbName}</b>
          </p>
          <button className="term-btn term-btn--solid" disabled={busy} onClick={runStep2}>
            {busy ? 'seeding...' : 'run_step_2 →'}
          </button>
          {seedStatus && (
            <div className="kv-grid" style={{ marginTop: 16 }}>
              <dt>totalTables</dt><dd>{seedStatus.totalTables}</dd>
              <dt>formCount</dt><dd>{seedStatus.formCount}</dd>
              <dt>moduleCount</dt><dd>{seedStatus.moduleCount}</dd>
            </div>
          )}
        </Panel>
      )}

      {step === 3 && (
        <Panel title="// STEP_3 — register_tenant (Master DB)">
          <div className="form-grid">
            <div className="field">
              <label>clientCode *</label>
              <input className="term-input" value={configureForm.clientCode}
                onChange={(e) => setConfigureForm({ ...configureForm, clientCode: e.target.value })} />
            </div>
            <div className="field">
              <label>clientName</label>
              <input className="term-input" value={configureForm.clientName}
                onChange={(e) => setConfigureForm({ ...configureForm, clientName: e.target.value })} />
            </div>
            <div className="field">
              <label>emailId</label>
              <input className="term-input" value={configureForm.emailId}
                onChange={(e) => setConfigureForm({ ...configureForm, emailId: e.target.value })} />
            </div>
            <div className="field">
              <label>mobileNo</label>
              <input className="term-input" value={configureForm.mobileNo}
                onChange={(e) => setConfigureForm({ ...configureForm, mobileNo: e.target.value })} />
            </div>
            <div className="field field--full">
              <label>address</label>
              <input className="term-input" value={configureForm.address}
                onChange={(e) => setConfigureForm({ ...configureForm, address: e.target.value })} />
            </div>
            <div className="field">
              <label>websiteLink</label>
              <input className="term-input" value={configureForm.websiteLink}
                onChange={(e) => setConfigureForm({ ...configureForm, websiteLink: e.target.value })} />
            </div>
            <div className="field">
              <label>logoPath</label>
              <input className="term-input" value={configureForm.logoPath}
                onChange={(e) => setConfigureForm({ ...configureForm, logoPath: e.target.value })} />
            </div>
            <div className="field">
              <label>noOfUsers</label>
              <input type="number" className="term-input" value={configureForm.noOfUsers}
                onChange={(e) => setConfigureForm({ ...configureForm, noOfUsers: e.target.value })} />
            </div>
            <div className="field">
              <label>fineYear</label>
              <input className="term-input" value={configureForm.fineYear}
                onChange={(e) => setConfigureForm({ ...configureForm, fineYear: e.target.value })} />
            </div>
            <div className="field">
              <label>expiryDate <span className="field-hint">(defaults to +1 year)</span></label>
              <input type="date" className="term-input" value={configureForm.expiryDate}
                onChange={(e) => setConfigureForm({ ...configureForm, expiryDate: e.target.value })} />
            </div>
            <div className="field">
              <label>dbServer <span className="field-hint">(optional)</span></label>
              <input className="term-input" placeholder="localhost\SQLEXPRESS" value={configureForm.dbServer}
                onChange={(e) => setConfigureForm({ ...configureForm, dbServer: e.target.value })} />
            </div>
          </div>
          <button
            className="term-btn term-btn--solid"
            disabled={busy || !configureForm.clientCode}
            onClick={runStep3}
          >
            {busy ? 'registering...' : 'run_step_3 →'}
          </button>
        </Panel>
      )}

      {step === 4 && (
        <Panel title="// STEP_4 — admin_credentials">
          <div className="form-grid">
            <div className="field">
              <label>newUsername (3–50 chars, letters/digits/underscore)</label>
              <input className="term-input" value={adminForm.newUsername}
                onChange={(e) => setAdminForm({ ...adminForm, newUsername: e.target.value })} />
            </div>
            <div className="field">
              <label>userType</label>
              <select className="term-select" value={adminForm.userType}
                onChange={(e) => setAdminForm({ ...adminForm, userType: e.target.value })}>
                <option>Admin</option>
                <option>Employee</option>
              </select>
            </div>
            <div className="field field--full">
              <label>password</label>
              <input type="password" className="term-input" value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} />
              <div className="field-hint">min 8 chars · 1 uppercase · 1 lowercase · 1 digit · 1 special char</div>
              {pwError && <div className="field-error">{pwError}</div>}
            </div>
            <div className="field">
              <label>expiryDate</label>
              <input type="date" className="term-input" value={adminForm.expiryDate}
                onChange={(e) => setAdminForm({ ...adminForm, expiryDate: e.target.value })} />
            </div>
          </div>
          <button className="term-btn term-btn--solid" disabled={busy} onClick={runStep4}>
            {busy ? 'saving...' : 'run_step_4 →'}
          </button>
        </Panel>
      )}

      {step === 5 && (
        <Panel title="// STEP_5 — organization_profile">
          <div className="form-grid">
            <div className="field field--full">
              <label>organisationName</label>
              <input className="term-input" value={orgForm.organisationName}
                onChange={(e) => setOrgForm({ ...orgForm, organisationName: e.target.value })} />
            </div>
            <div className="field">
              <label>email</label>
              <input className="term-input" value={orgForm.email}
                onChange={(e) => setOrgForm({ ...orgForm, email: e.target.value })} />
            </div>
            <div className="field">
              <label>mobileNo</label>
              <input className="term-input" value={orgForm.mobileNo}
                onChange={(e) => setOrgForm({ ...orgForm, mobileNo: e.target.value })} />
            </div>
            <div className="field">
              <label>landlineNo</label>
              <input className="term-input" value={orgForm.landlineNo}
                onChange={(e) => setOrgForm({ ...orgForm, landlineNo: e.target.value })} />
            </div>
            <div className="field field--full">
              <label>address</label>
              <input className="term-input" value={orgForm.address}
                onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })} />
            </div>
          </div>
          <button className="term-btn term-btn--solid" disabled={busy} onClick={runStep5}>
            {busy ? 'finishing...' : 'finish_provisioning ✓'}
          </button>
        </Panel>
      )}
    </>
  );
}
